<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { usePlanStore } from "@/stores/usePlanStore";

import { useDraggableModal } from "@/composables/useDraggableModal";

const planStore = usePlanStore();
const emit = defineEmits(["close", "refresh"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const obj = ref({
  name: "",
  description: "",
  mpPlanId: "",
  price: 0.0,
  trialDays: 0,
  billingCycle: "Monthly",
  isActive: false,
  // ✅ NOVO (IsPublic): padrão true — novos planos são públicos por padrão
  isPublic: true,
  // ✅ NOVO: UsageType e MaxManagedAccounts
  usageType: "Individual",
  maxManagedAccounts: 0,
  maxGroupUser: 1,
  maxTextCharacters: 1,
  maxNoteCharacters: 1,
  canExport: false,
  hasDashboard: false,
});

const apiError = ref(null);
const isSubmitting = ref(false);
const planNameInputRef = ref(null);

const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

async function handleSubmit() {
  if (isSubmitting.value) return;

  apiError.value = null;
  isSubmitting.value = true;

  try {
    await planStore.createPlan(obj.value);

    showSuccessToast("Plano criado com sucesso.");

    setTimeout(() => {
      emit("refresh");
      emit("close");
      isSubmitting.value = false;
    }, 2400);
  } catch (error) {
    const errorData = error.response?.data;
    let userFriendlyMessage =
      errorData?.message || "Ocorreu um erro desconhecido ao criar o plano.";

    if (errorData && errorData.errors) {
      const messages = Object.values(errorData.errors).flat();
      if (messages.length > 0) {
        userFriendlyMessage = messages.join("\n");
      }
    }

    apiError.value = userFriendlyMessage;
    console.error("Erro detalhado da API:", error.response?.data || error);

    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

const closeModal = () => {
  if (!isSubmitting.value) {
    emit("close");
  }
};

const handleEscKey = (event) => {
  if (event.key === "Escape") closeModal();
};

onMounted(async () => {
  document.addEventListener("keydown", handleEscKey);

  await nextTick();
  if (planNameInputRef.value) {
    planNameInputRef.value.focus();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div class="modal-backdrop" @click.self="!isSubmitting">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Criar Novo Plano</h3>

        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          :disabled="isSubmitting"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" novalidate>
        <div class="modal-body">
          <div class="form-grid">
            <div class="form-column">
              <div class="form-group">
                <label for="name" class="form-label">Nome do Plano</label>
                <input
                  ref="planNameInputRef"
                  v-model="obj.name"
                  type="text"
                  id="name"
                  class="input-base"
                  placeholder="Ex: Premium"
                  required
                />
              </div>

              <div class="form-group">
                <label for="description" class="form-label">Descrição</label>
                <textarea
                  v-model="obj.description"
                  id="description"
                  class="input-base textarea-base"
                  rows="3"
                  placeholder="Descreva os benefícios do plano"
                ></textarea>
              </div>

              <div class="form-group">
                <label for="mpPlanId" class="form-label">MP Plan ID</label>
                <input
                  v-model="obj.mpPlanId"
                  type="text"
                  id="mpPlanId"
                  class="input-base"
                  placeholder="Ex: 2d7305414ce947efa952b78c23e69138"
                />
              </div>

              <div class="form-group">
                <label for="price" class="form-label">Preço (R$)</label>
                <input
                  v-model.number="obj.price"
                  type="number"
                  step="0.01"
                  id="price"
                  class="input-base"
                  placeholder="Ex: 29.90"
                  required
                />
              </div>
            </div>

            <div class="form-column">
              <div class="form-group">
                <label for="billingCycle" class="form-label">
                  Ciclo de Cobrança
                </label>
                <select v-model="obj.billingCycle" id="billingCycle" class="select-base">
                  <option value="Monthly">Mensal</option>
                  <option value="Yearly">Anual</option>
                  <option value="OneTime">Pagamento Único</option>
                </select>
              </div>

              <!-- ✅ NOVO: UsageType e MaxManagedAccounts -->
              <div class="form-group">
                <label for="usageType" class="form-label">Usage Type</label>
                <select v-model="obj.usageType" id="usageType" class="select-base">
                  <option value="Individual">Individual</option>
                  <option value="Family">Family</option>
                  <option value="Edu">Edu</option>
                </select>
              </div>

              <div class="form-group">
                <label for="maxManagedAccounts" class="form-label">Max Managed Accounts</label>
                <input
                  v-model.number="obj.maxManagedAccounts"
                  type="number"
                  id="maxManagedAccounts"
                  class="input-base"
                  min="0"
                />
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div class="form-group">
                  <label for="trialDays" class="form-label">Dias de Teste</label>
                  <input
                    v-model.number="obj.trialDays"
                    type="number"
                    id="trialDays"
                    class="input-base"
                  />
                </div>

                <div class="form-group">
                  <label for="maxGroupUser" class="form-label">Usuários</label>
                  <input
                    v-model.number="obj.maxGroupUser"
                    type="number"
                    id="maxGroupUser"
                    class="input-base"
                  />
                </div>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div class="form-group">
                  <label for="maxTextCharacters" class="form-label">Textos</label>
                  <input
                    v-model.number="obj.maxTextCharacters"
                    type="number"
                    id="maxTextCharacters"
                    class="input-base"
                  />
                </div>

                <div class="form-group">
                  <label for="maxNoteCharacters" class="form-label">Notas</label>
                  <input
                    v-model.number="obj.maxNoteCharacters"
                    type="number"
                    id="maxNoteCharacters"
                    class="input-base"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="checkbox-grid">
            <div class="form-group-checkbox">
              <input
                v-model="obj.isActive"
                type="checkbox"
                id="isActive"
                class="checkbox-style"
              />
              <label for="isActive">Plano Ativo</label>
            </div>

            <!-- ✅ NOVO (IsPublic): aparece na listagem pública -->
            <div class="form-group-checkbox">
              <input
                v-model="obj.isPublic"
                type="checkbox"
                id="isPublic"
                class="checkbox-style"
              />
              <label for="isPublic">Plano Público</label>
            </div>

            <div class="form-group-checkbox">
              <input
                v-model="obj.canExport"
                type="checkbox"
                id="canExport"
                class="checkbox-style"
              />
              <label for="canExport">Permite Exportar</label>
            </div>

            <div class="form-group-checkbox">
              <input
                v-model="obj.hasDashboard"
                type="checkbox"
                id="hasDashboard"
                class="checkbox-style"
              />
              <label for="hasDashboard">Acesso ao Dashboard</label>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>

          <button type="submit" class="btn btn-success" :disabled="isSubmitting">
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>

            <svg
              v-else
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"
              ></path>
            </svg>

            <span>{{ isSubmitting ? "Criando..." : "Criar Plano" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.modal-panel {
  max-width: 42rem;
  max-height: calc(100vh - 4rem);
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
}
@media (min-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.form-column {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.textarea-base {
  resize: vertical;
  min-height: 92px;
  line-height: 1.35;
}

.checkbox-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
  margin-top: 1rem;
  border-top: 1px solid var(--border-color);
  padding-top: 1rem;
}

.form-group-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.6rem;
  border-radius: 10px;
  background: var(--surface-2);
  border: 1px solid var(--neutral-200);
}

.form-group-checkbox label {
  font-weight: 500;
  color: var(--text-dark);
  font-size: 0.92rem;
}

.checkbox-style {
  width: 1rem;
}

.modal-footer {
  bottom: 0;
}

.api-error-message {
  padding: 0.75rem 0.9rem;
  background-color: var(--danger-bg);
  color: var(--danger-text);
  border-radius: 10px;
  text-align: left;
  border-left: 6px solid var(--danger);
  white-space: pre-wrap;
  font-size: 0.92rem;
}

.select-base {
  width: 100%;
  height: 34px;
  padding: 0 0.75rem;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 0.825rem;
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.select-base:focus {
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}

.select-base:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>